import { z } from 'zod';

/**
 * The router's search codec and the field types the route schemas build from. The two halves are one
 * contract: the URL carries readable, hand-editable values (`?audience=personal-agent,group-agent`,
 * `?calendar=true`), and components see real types (`string[]`, `boolean`, `number`).
 *
 * TanStack's default codec JSON-encodes each value instead, so a list would go out as
 * `%5B%22a%22%5D` and `?user=12345` would come back as a number. Links in Linear tickets and Slack
 * threads predate this router, so the byte format is the one they were written in.
 *
 * Parsing hands every route schema the raw strings; the field types below turn them into values.
 * They accept the typed value as well as its string, because a navigation validates the object it
 * was given rather than re-parsing the URL it wrote.
 */

const LIST_SEPARATOR = ',';

export const parseSearch = (searchStr: string): Record<string, string> =>
  Object.fromEntries(new URLSearchParams(searchStr));

/** Absent, empty, `false` and `[]` all leave the param out, so clearing a filter removes it from the URL. */
const serializeValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === false || value === '') {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(LIST_SEPARATOR) : undefined;
  }
  return String(value);
};

export const stringifySearch = (searchObj: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchObj)) {
    const serialized = serializeValue(value);
    if (serialized !== undefined) {
      params.set(key, serialized);
    }
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
};

/** A free-form string, such as an id or a search term. Blank reads as absent. */
export const stringParam = z
  .string()
  .optional()
  .transform((value) => value || undefined);

/**
 * A comma-joined list. Entries `item` rejects are dropped rather than failing the route, so a link
 * that names a since-removed option still opens with the rest of its filters.
 */
export const listOf = <T extends string>(item: z.ZodType<T, T>) =>
  z
    .union([z.array(item), z.string()])
    .optional()
    .transform((value): T[] => {
      const entries: string[] = typeof value === 'string' ? value.split(LIST_SEPARATOR) : (value ?? []);
      return entries.filter((entry): entry is T => entry !== '' && item.safeParse(entry).success);
    });

export const listParam = listOf(z.string());

/** `true` when present as `true`; absent otherwise. */
export const flagParam = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform((value) => value === true || value === 'true');

/** A non-negative integer; anything else reads as absent rather than pinning a view at row zero. */
export const indexParam = z
  .union([z.number(), z.string()])
  .optional()
  .transform((value) => {
    const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    return parsed !== undefined && Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
  });
